/**
 * A dead sign-in link has to say so.
 *
 * ⚠⚠ The failure this exists for: a client asked for an email sign-in link,
 * clicked it, and landed on the **marketing home page** with no session and no
 * explanation — as though he had typed karr.pro into the address bar himself.
 * He assumed he had picked the wrong button. He had not.
 *
 * The mechanism: `emailRedirectTo` sends the link back to `window.location.origin`,
 * i.e. `/`, and `/` is a fork (`SmartLanding` in router.tsx) that shows the
 * marketing page to anyone without a session. When GoTrue cannot honour the
 * link it redirects to exactly that URL with the reason in the hash —
 *
 *   /#error=access_denied&error_code=otp_expired&error_description=Email+link+is+invalid+or+has+expired
 *
 * — and until this module, NOTHING in `src` read those params. A burned link and
 * a cold visit rendered the same pixel, so the one failure a user cannot debug
 * was also the one Karr never mentioned.
 *
 * Why a link burns, in order of how often it actually happens:
 *  1. An email client or security gateway **opened the link to scan it**. A
 *     magic-link token is single-use, so the scan spends it and the human's
 *     click arrives second. Shared-inbox webmail (Front/Missive/Zoho) does this.
 *  2. It expired — GoTrue's default is one hour.
 *  3. The link was already used once, successfully, on another device.
 *
 * Karr uses the **implicit** flow (auth-js's default, not overridden in
 * `db/supabase.ts`), so the params arrive in the URL hash. The query string is
 * read too because a PKCE-shaped redirect error puts them there instead, and
 * this should keep working if the flow is ever switched.
 *
 * Ordering note: auth-js also sees this hash, throws `AuthImplicitGrantRedirectError`
 * internally, and — unlike the success path — does **not** clear the hash
 * (`window.location.hash = ''` sits after the throw). So the params survive for
 * us to read, and clearing them is our job.
 *
 * Since 2026-08-11 this module owns one more reason to be looking at the
 * sign-in screen: an **expired session**, spotted by `useAuth` when the
 * membership read comes back with a dead JWT. It lives here rather than in a
 * module of its own because the storage slot and the surface that renders it
 * are the same ones, and a second writer of one sessionStorage key is how two
 * explanations end up racing for the same paragraph.
 *
 * @since 2026-08-01
 */

/** Survives the redirect to /auth/signin; sessionStorage, so it dies with the tab. */
const STORAGE_KEY = 'karr_auth_link_error';

/** The code stashed when the session, not the link, is what expired. */
export const SESSION_EXPIRED_CODE = 'session_expired';

/**
 * Which way out this failure has.
 *
 * `fresh-link` — the link is dead and only a new one will do, so the screen owes
 * a button that sends one. `sign-in` — the link was never the problem (the
 * session timed out), so the form already on the screen IS the way out and a
 * second button would only compete with it.
 */
export type AuthLinkRemedy = 'fresh-link' | 'sign-in';

export interface AuthLinkFailure {
    /** GoTrue's `error_code` (or `error` when no code was sent). */
    code: string;
    /** WHAT HAPPENED. One short sentence, no cause, no instruction. */
    message: string;
    /** WHAT TO DO. One line, imperative. */
    do: string;
    /** Which button the screen owes them. */
    remedy: AuthLinkRemedy;
    /**
     * The cause they cannot see, in small print under the button.
     * Optional because most failures have no hidden cause worth explaining.
     */
    hint?: string;
}

/**
 * The one sentence that stops a dead link feeling like the person's own fault.
 *
 * ⚠ Kept out of `message` on purpose. It was IN the sentence until 3 Sep 2026,
 * and the result was six lines of prose on a phone with the only actionable
 * words ("ask for a fresh one below") last. Cause is small print; the action is
 * a button.
 */
const SCANNER_HINT =
    'This is usually not your mistake. Many office mail systems open links themselves to '
    + 'check they are safe, and that one automatic click uses up a link that only works once. '
    + 'If it keeps happening, set a password and you will never need a link again.';

/**
 * The three answers a given failure deserves: what happened, what to do, and
 * which button the screen must show.
 *
 * Deliberately says what to DO. "access_denied" is true and useless; "the link
 * was already used, here is a fresh one" is what the person needs.
 */
function failureFor(code: string, description: string | null): Omit<AuthLinkFailure, 'code'> {
    switch (code) {
        case 'otp_expired':
            return {
                message: 'That sign-in link has already been used, or it has expired.',
                do: 'A link works only once and lasts about an hour. Get yourself a fresh one.',
                remedy: 'fresh-link',
                hint: SCANNER_HINT,
            };
        case 'access_denied':
            return {
                message: 'That sign-in link did not work. It was either already used or it expired.',
                do: 'Get yourself a fresh one.',
                remedy: 'fresh-link',
                hint: SCANNER_HINT,
            };
        case 'server_error':
            return {
                message: 'Something went wrong at our end while signing you in.',
                do: 'Get yourself a fresh link. If it happens again, tell us.',
                remedy: 'fresh-link',
            };
        case SESSION_EXPIRED_CODE:
            // Not a link at all, so it gets no link button. Says the one thing
            // that matters most to somebody who was working a moment ago: the
            // books are exactly where they left them.
            return {
                message: 'Your sign-in expired, so Karr signed you out. Nothing has been lost.',
                do: 'Sign in again below and your books are exactly as you left them.',
                remedy: 'sign-in',
            };
        default:
            return {
                message: description ? `${description.replace(/\.$/, '')}.` : 'That sign-in link did not work.',
                do: 'Get yourself a fresh link.',
                remedy: 'fresh-link',
            };
    }
}

/**
 * Read a GoTrue failure out of a URL's hash and query — pure, so the mapping is
 * testable without a browser.
 *
 * @param hash - `window.location.hash`, with or without the leading `#`
 * @param search - `window.location.search`, with or without the leading `?`
 * @returns the failure, or null when this is not an auth-error redirect
 */
export function readAuthLinkError(hash: string, search: string): AuthLinkFailure | null {
    for (const raw of [hash, search]) {
        const body = raw.replace(/^[#?]/, '');
        if (!body) continue;
        const params = new URLSearchParams(body);

        // A successful redirect carries the tokens — never treat that as a failure,
        // and never touch a URL auth-js still needs.
        if (params.get('access_token')) return null;

        const error = params.get('error');
        const code = params.get('error_code');
        const description = params.get('error_description');
        // Require a GoTrue-shaped pair, so an unrelated `?error=` on some other
        // route can't hijack the sign-in screen.
        if (!error && !code) continue;
        if (!code && !description) continue;

        const resolved = code || error || 'unspecified';
        return { code: resolved, ...failureFor(code || error || '', description) };
    }
    return null;
}

/**
 * Called once at boot, before anything renders.
 *
 * Stashes the failure and strips it from the address bar, so a reload does not
 * replay a stale error and the user is never left staring at a URL full of
 * `error_description=Email+link+is+invalid`.
 *
 * @returns the failure it captured, if any
 */
export function captureAuthLinkError(): AuthLinkFailure | null {
    if (typeof window === 'undefined') return null;
    const failure = readAuthLinkError(window.location.hash, window.location.search);
    if (!failure) return null;

    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(failure));
    } catch { /* private mode — we still clean the URL and show the generic path */ }

    try {
        const url = new URL(window.location.href);
        url.hash = '';
        for (const k of ['error', 'error_code', 'error_description']) url.searchParams.delete(k);
        window.history.replaceState(window.history.state, '', url.toString());
    } catch { /* ignore */ }

    return failure;
}

/**
 * Is this the database refusing us because the sign-in is dead?
 *
 * PostgREST answers an expired or malformed JWT with `PGRST301` / HTTP 401.
 * `42501` is deliberately NOT here: that is RLS declining a row, which means
 * the session is alive and working exactly as designed.
 *
 * Worth separating because the two failures deserve opposite screens. A blip
 * deserves "Try again"; a dead session can be retried until the heat death of
 * the universe and will fail every time, so the only honest button is "sign in".
 */
export function isSessionExpired(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const e = error as { code?: string; status?: number; message?: string };
    if (e.code === 'PGRST301') return true;
    if (e.status === 401) return true;
    return /\bjwt\b.*\b(expired|invalid|malformed)\b/i.test(String(e.message ?? ''));
}

/**
 * Remember that the SESSION expired, for the sign-in screen to explain.
 *
 * Same slot as a dead link, because it answers the same question the person is
 * actually asking: why am I looking at this screen?
 */
export function noteSessionExpired(): void {
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
            code: SESSION_EXPIRED_CODE,
            ...failureFor(SESSION_EXPIRED_CODE, null),
        } satisfies AuthLinkFailure));
    } catch { /* private mode — they still land on sign-in, just without the sentence */ }
}

/** Is there a captured failure waiting to be shown? Does not consume it. */
export function hasAuthLinkError(): boolean {
    try {
        return window.sessionStorage.getItem(STORAGE_KEY) !== null;
    } catch {
        return false;
    }
}

/**
 * Take the captured failure and clear it — the sign-in screen calls this once on
 * mount, so the message shows for the arrival it belongs to and not forever after.
 */
export function consumeAuthLinkError(): AuthLinkFailure | null {
    try {
        const raw = window.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        window.sessionStorage.removeItem(STORAGE_KEY);
        return JSON.parse(raw) as AuthLinkFailure;
    } catch {
        return null;
    }
}
